import { Component, computed, effect, inject } from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { filter, map, tap } from 'rxjs';
import { OrderLayoutService } from '../../../common/services/order/order-layout.service';
import { OrderBaseComponent } from '../order-base.component';
import { CartStateService } from '../../../common/store/state/order/orderv2/workflows/order-cart/order-cart.state.service';
import { ConfirmOrLeave } from '../../../common/guards/confirm-or-leave.guard';

@Component({
  selector: 'app-order-layout',
  standalone: false,
  templateUrl: './order-layout.component.html',
  styleUrl: './order-layout.component.scss'
})
export class OrderLayoutComponent extends OrderBaseComponent implements ConfirmOrLeave {

  displayLeaveConfirmation = false;
  allowNavigation = false;
  resolveNavigation: ((value: boolean) => void) | null = null;
  isLeavingOrder = false;
  cartStateService = inject(CartStateService);
  private orderLayoutService: OrderLayoutService = inject(OrderLayoutService);

  customer = computed(() => {
    return this.orderApiResponseStateService.customer();
  })

  business = computed(() => {
    return this.order()?.serviceInfoCollection?.businessInformation
  })


  pageHeader = this.orderLayoutService.pageHeader()
  subHeader = this.orderLayoutService.subHeader()

  constructor() {
    super()

    // Track destination URL BEFORE navigation starts (for canDeactivate guard)
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
        tap(event => {
          const navStart = event as NavigationStart;
          const destinationUrl = navStart.url;
          this.isLeavingOrder = !destinationUrl.includes('/order');
        })
      ).subscribe()
    );

    this.subscriptions.add(this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let child = this.route.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          return child?.snapshot.data;
        }),
        tap(data => {
          this.orderLayoutService.pageHeader.set(data?.title)
          this.orderLayoutService.subHeader.set(data?.subHeader)
        })
      )
      .subscribe());

    effect(() => {
      this.pageHeader = this.orderLayoutService.pageHeader()
      this.subHeader = this.orderLayoutService.subHeader()
    })

    effect(() => {
      const creditCheckHeader = this.orderLayoutService.creditCheckSignal()
      if (creditCheckHeader) {
        this.orderLayoutService.pageHeader.set(this.orderLayoutService.creditCheckTemplate.find(x => x.name === creditCheckHeader)?.title)
        this.orderLayoutService.subHeader.set(this.orderLayoutService.creditCheckTemplate.find(x => x.name === creditCheckHeader)?.subHeader)
      }
    })
  }

  canDeactivate(): Promise<boolean> | boolean {
    if (this.allowNavigation) {
      this.allowNavigation = false;
      return true;
    }

    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['skipLeaveConfirmation']) {
      return true;
    }

    // Only show confirmation if actually leaving the order route
    if (!this.isLeavingOrder) {
      return true;
    }

    this.displayLeaveConfirmation = true;
    return new Promise<boolean>((resolve) => {
      this.resolveNavigation = resolve;
    });
  }

  stay() {
    this.displayLeaveConfirmation = false;
    if (this.resolveNavigation) {
      this.resolveNavigation(false);
      this.resolveNavigation = null;
    }
  }

  leave() {
    this.orderStateService.reset();
    this.allowNavigation = true;
    this.displayLeaveConfirmation = false;
    if (this.resolveNavigation) {
      this.resolveNavigation(true);
      this.resolveNavigation = null;
    }
  }

}
